import { XMarkIcon } from "@heroicons/react/24/solid";
import React, { memo } from "react";
import { generatePath } from "react-router-dom";
import CheckSVG from "../../../../assets/svg/check.svg";
import GoogleDocsSVG from "../../../../assets/svg/google-docs.svg";
import ShopifySVG from "../../../../assets/svg/shopify.svg";
import WordpressSVG from "../../../../assets/svg/wordpress.svg";
import TruncatedText from "../../../../components/common/TruncatedText";
import Button from "../../../../components/form/Button";
import { ROUTE_DESTINATIONS_EDIT } from "../../../../constants/routes";
import {
  DestinationTypeEnum,
  DestinationTypeMap,
} from "../../../../enums/destination";
import {
  AutomationDaysEnum,
  AutomationDaysMap,
  BrandPersonaMap,
  RunStatusColorMap,
  RunStatusMap,
  TemplateStatusEnum,
} from "../../../../enums/template";
import { DestinationInfo, Template, TemplateDetails } from "../../../../models";
import { formatTimeHHMM, getSourceSVG, sortNumbers } from "../../../../utils";

export const TitleCell = memo(({ t }: { t: Template }) => (
  <div className="flex items-center gap-3">
    <img
      alt="source"
      src={getSourceSVG(t.sourceId)}
      className="size-5"
    />
    <TruncatedText
      id={`template-name-${t.templateId}`}
      text={t.name!}
    />
  </div>
));

export const BrandPersonaCell = memo(({ t }: { t: Template }) => (
  <div className="truncate overflow-hidden">
    {t.brandPersonaId && BrandPersonaMap[t.brandPersonaId]}
  </div>
));

export const AutoScheduleCell = memo(({ t }: { t: Template }) => {
  if (!t.isAutoEnabled) {
    return <XMarkIcon className="size-4 text-red-600 md:mx-auto" />;
  }
  const days = sortNumbers(t.autoDays ?? []);
  return (
    <div className="text-sm mx-auto truncate md:text-center">
      {days.map((d: AutomationDaysEnum, i) => (
        <span key={d}>
          {AutomationDaysMap[d]}
          {i < days.length - 1 ? ", " : ""}
        </span>
      ))}
      {" @ " + formatTimeHHMM(t.autoTime1!)}
      {t.autoTime2 ? ", " + formatTimeHHMM(t.autoTime2) : ""}
    </div>
  );
});

export const BoolIconCell = memo(
  ({ value, alt }: { value: boolean; alt: string }) => (
    <div className="mx-auto">
      {value ? (
        <img
          alt={alt}
          src={CheckSVG}
          className="size-4"
        />
      ) : (
        <XMarkIcon className="size-4 text-red-600" />
      )}
    </div>
  )
);

export const ArticlesCountCell = memo(
  ({ t, onView }: { t: TemplateDetails; onView: (name: string) => void }) => {
    const count = t.articleCount || 0;
    return (
      <div className="md:text-center">
        {count > 0 ? (
          <Button
            paddingZero
            variant="light-primary"
            className="p-1"
            onClick={(e: any) => {
              e.stopPropagation();
              onView(t.name!);
            }}
          >
            {count}
          </Button>
        ) : (
          count
        )}
      </div>
    );
  }
);

export const DestinationLogoCell = memo(
  ({ info }: { info: DestinationInfo }) => {
    const isWP = info.destinationTypeId === DestinationTypeEnum.WORDPRESS;
    const isShop = info.destinationTypeId === DestinationTypeEnum.SHOPIFY;
    const isGoogle = info.destinationTypeId === DestinationTypeEnum.GOOGLE_DOCS;
    return (
      <div className="flex md:justify-center">
        <Button
          variant="icon"
          paddingZero
          onClick={(e: any) => {
            e.stopPropagation();
            window.open(
              generatePath(ROUTE_DESTINATIONS_EDIT, {
                id: info.destinationId,
              }) + `?type=${DestinationTypeMap[info.destinationTypeId]}`,
              "_blank"
            );
          }}
        >
          {isWP && (
            <img
              alt="WordPress"
              src={WordpressSVG}
              className="size-5"
            />
          )}
          {isShop && (
            <img
              alt="Shopify"
              src={ShopifySVG}
              className="size-5"
            />
          )}
          {isGoogle && (
            <img
              alt="Google Docs"
              src={GoogleDocsSVG}
              className="size-5"
            />
          )}
        </Button>
      </div>
    );
  }
);

export const StatusCell = memo(
  ({ t, onView }: { t: Template; onView: (name: string) => void }) => {
    const status = t.templateStatusId;
    const color = RunStatusColorMap[status];
    const label = RunStatusMap[status];
    if (status === TemplateStatusEnum.COMPLETED) {
      return (
        <Button
          variant="transparent-primary"
          paddingZero
          className="flex items-center gap-1.5"
          onClick={(e: any) => {
            e.stopPropagation();
            onView(t.name!);
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: color }}
          />
          {label}
        </Button>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: color }}
        />
        {label}
      </div>
    );
  }
);

export const AccountTag = memo(
  ({ accountTagName }: { accountTagName: string }) => {
    if (!accountTagName) return null;

    return (
      <TruncatedText
        id={`account-tag-${accountTagName}`}
        text={accountTagName}
        className="px-1 py-0.5 text-xs rounded bg-gray-100/80 text-gray-600 border border-gray-300/80 w-max max-w-28"
      />
    );
  }
);
